import React from 'react';
import { FaSearch, FaHospital, FaPills, FaFlask, FaVideo, FaHistory } from 'react-icons/fa';
import { formatDateTime, currencyRounded, getStatusBadgeStyle, TRANSACTION_CATEGORY_KEYS } from '../../utils/subscriberHelpers';
import Pagination from '../common/Pagination';

const TransactionHistoryTable = ({
  transactions,
  txnSearch,
  setTxnSearch,
  txnCategory,
  setTxnCategory,
  txnPage,
  txnPagination,
  setTxnPage,
  isOpsOrSuperAdmin,
  onTransactionClick,
  isLoading
}) => {
  const txnTotalPages = Math.max(1, Math.ceil((txnPagination.total || 0) / (txnPagination.items || 25)));
  const txnTotalItems = txnPagination.total || 0;
  const txnStartIdx = ((txnPage - 1) * (txnPagination.items || 25));
  const subscriberId = window.location.pathname.split('/').filter(Boolean).pop();

  const ACTION_BTNS = [
    { label: 'Hospital', path: 'hospital-payment', icon: <FaHospital size={13} />, color: 'bg-green-500 hover:bg-green-600 shadow-green-200' },
    { label: 'Medicine', path: 'medicine-transaction', icon: <FaPills size={13} />, color: 'bg-blue-500 hover:bg-blue-600 shadow-blue-200' },
    { label: 'Diagnostic', path: 'diagnostic-transaction', icon: <FaFlask size={13} />, color: 'bg-orange-500 hover:bg-orange-600 shadow-orange-200' },
    { label: 'Consultation', path: 'virtual-consultation', icon: <FaVideo size={13} />, color: 'bg-purple-500 hover:bg-purple-600 shadow-purple-200' },
  ];

  // Keys come from the shared list so the tab strip and the back-navigation URL round-trip
  // cannot drift apart; only the one key that needs a shorter label overrides it.
  const TAB_LABELS = { 'Hospital Cash Allowance': 'Cash Allowance' };
  const TABS = TRANSACTION_CATEGORY_KEYS.map((key) => ({ key, label: TAB_LABELS[key] || key }));

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">

      {/* ── Header ── */}
      <div className="px-6 pt-5 pb-4 border-b border-gray-100">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center shrink-0">
              <FaHistory className="text-gray-500" size={14} />
            </div>
            <div>
              <p className="text-base font-bold text-gray-900 leading-tight">Transaction History</p>
              <p className="text-xs text-gray-400 mt-0.5">
                {txnTotalItems > 0 ? `${txnTotalItems} record${txnTotalItems !== 1 ? 's' : ''}` : 'No records yet'}
              </p>
            </div>
          </div>

          {isOpsOrSuperAdmin && (
            <div className="flex items-center gap-2.5 flex-wrap justify-end">
              {ACTION_BTNS.map(({ label, path, icon, color }) => (
                <button
                  key={path}
                  onClick={() => window.location.href = `/subscriber-wallet/subscriber/${subscriberId}/${path}`}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl text-white shadow-md transition-all active:scale-[0.97] ${color}`}
                >
                  <span className="text-white/80">{icon}</span>{label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Search */}
        <div className="relative">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={12} />
          <input
            type="text"
            placeholder="Search by name, mobile, ref code or service…"
            className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 bg-gray-50 placeholder-gray-400 transition-all"
            value={txnSearch}
            onChange={(e) => setTxnSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setTxnPage(1); } }}
          />
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="px-6 border-b border-gray-100 flex items-center overflow-x-auto scrollbar-hide">
        {TABS.map(({ key, label }) => {
          const isActive = txnCategory === key;
          return (
            <button
              key={key}
              onClick={() => { setTxnCategory(key); setTxnPage(1); }}
              className={`relative flex items-center gap-1.5 px-4 py-3 text-sm font-medium whitespace-nowrap transition-all shrink-0 ${isActive ? 'text-blue-600' : 'text-gray-500 hover:text-gray-800'
                }`}
            >
              {label}
              {isActive && <span className="absolute bottom-0 inset-x-0 h-0.5 bg-blue-500 rounded-t-full" />}
            </button>
          );
        })}
      </div>

      {/* ── Table ── */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-max">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/70">
              <th className="px-5 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">Date & Time</th>
              <th className="px-5 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">Status</th>
              <th className="px-5 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">Name</th>
              <th className="px-5 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">Provider</th>
              <th className="px-5 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">Service Type</th>
              <th className="px-5 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">Payment Method</th>
              <th className="px-5 py-3 text-right text-[11px] font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">Price</th>
              <th className="px-5 py-3 text-right text-[11px] font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">Cashback</th>
              <th className="px-5 py-3 text-right text-[11px] font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">WB</th>
              <th className="px-5 py-3 text-right text-[11px] font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">Credit Balance</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {isLoading ? (
              <tr>
                <td colSpan="10" className="px-5 py-12 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                    <p className="text-sm text-gray-400">Loading transactions…</p>
                  </div>
                </td>
              </tr>
            ) : transactions.length === 0 ? (
              <tr>
                <td colSpan="10" className="px-5 py-14 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center">
                      <FaHistory className="text-gray-300" size={16} />
                    </div>
                    <p className="text-sm font-medium text-gray-400">No transactions found</p>
                    {txnSearch && <p className="text-xs text-gray-300">Try clearing your search</p>}
                  </div>
                </td>
              </tr>
            ) : (
              transactions.map((t) => {
                let dateTimeValue = t.date_time || 'N/A';
                if (typeof dateTimeValue === 'object' && dateTimeValue !== null) {
                  dateTimeValue = (dateTimeValue.start_date && dateTimeValue.end_date)
                    ? `${dateTimeValue.start_date} to ${dateTimeValue.end_date}`
                    : 'N/A';
                }
                const displayDateTime = formatDateTime(dateTimeValue);
                const [datePart, ...timeParts] = displayDateTime.split(' ');
                const timePart = timeParts.join(' ');

                return (
                  <tr
                    key={t.booking_id || t.transaction_id || t.id}
                    className="hover:bg-blue-50/40 cursor-pointer transition-colors group"
                    onClick={() => { if (t.booking_id || t.transaction_id) onTransactionClick(t); }}
                  >
                    <td className="px-5 py-3 whitespace-nowrap">
                      <div className="text-xs font-medium text-gray-700">{datePart}</div>
                      {timePart && <div className="text-[11px] text-gray-400 mt-0.5">{timePart}</div>}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      <span
                        className="inline-flex px-2.5 py-1 text-xs font-semibold rounded-full border"
                        style={getStatusBadgeStyle(t.status)}
                      >
                        {t.status || 'N/A'}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-xs font-semibold text-gray-900 whitespace-nowrap">{t.subscriber_name || 'N/A'}</td>
                    <td className="px-5 py-3 text-xs text-gray-600 whitespace-nowrap capitalize">{t.service_provider || '—'}</td>
                    <td className="px-5 py-3 text-xs text-gray-600 whitespace-nowrap capitalize">{t.service_type || '—'}</td>
                    <td className="px-5 py-3 text-xs text-gray-600 whitespace-nowrap">{t.payment_method || '—'}</td>
                    <td className="px-5 py-3 text-xs font-bold text-gray-900 whitespace-nowrap text-right tabular-nums">
                      ₹{(t.price || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-5 py-3 text-xs font-semibold text-gray-700 whitespace-nowrap text-right tabular-nums">
                      {currencyRounded(t.cashback || 0)}
                    </td>
                    <td className="px-5 py-3 text-xs font-semibold text-gray-700 whitespace-nowrap text-right tabular-nums">{currencyRounded(t.wallet_balance || 0)}</td>
                    <td className="px-5 py-3 text-xs font-semibold text-gray-700 whitespace-nowrap text-right tabular-nums">
                      {t.credit_balance != null ? currencyRounded(t.credit_balance) : '—'}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ── */}
      {txnPagination.total > 0 && (
        <div className="px-6 py-4 border-t border-gray-100">
          <Pagination
            currentPage={txnPage}
            totalPages={txnTotalPages}
            totalItems={txnTotalItems}
            startIdx={txnStartIdx}
            pageSize={txnPagination.items || 25}
            onPageChange={setTxnPage}
          />
        </div>
      )}
    </div>
  );
};

export default TransactionHistoryTable;
